import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Clock, MapPin, Users, Wind, Droplets, Thermometer, Check, UserPlus, Settings2, Flag, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { MODALITIES, relativeMinutes, paceLabel, type Modality } from "@/lib/modality";
import { RideMap } from "@/components/RideMap";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { EditRideSheet } from "@/components/EditRideSheet";

export const Route = createFileRoute("/ride/$rideId")({
  component: RideDetail,
});

interface RideRow {
  id: string;
  creator_id: string;
  modality: Modality;
  pace: string;
  start_time: string;
  start_lat: number;
  start_lng: number;
  start_address: string | null;
  estimated_km: number | null;
  estimated_duration_min: number | null;
  description: string | null;
  club_id: string | null;
  port_id: string | null;
}

interface Profile { id: string; nickname: string; avatar_url: string | null; }
interface Weather { temp: string; wind: string; rain: string; }
interface ClubInfo { id: string; name: string; logo_url: string | null; is_verified: boolean; }
interface PortInfo {
  id: string;
  name: string;
  slope_side: string | null;
  length_km: number | null;
  elevation_gain_m: number | null;
  avg_gradient: number | null;
  category: string | null;
  surface: string | null;
  altitude_m: number | null;
}

function categoryColor(category: string | null) {
  switch (category) {
    case "HC": return "#ef4444";
    case "1": return "#f97316";
    case "2": return "#F5C518";
    case "3": return "#22c55e";
    case "4": return "#3b82f6";
    default: return "#9ca3af";
  }
}

function difficultyStars(n: number | null) {
  if (!n) return "";
  return "★".repeat(Math.max(0, Math.min(5, n))) + "☆".repeat(Math.max(0, 5 - n));
}

function RideDetail() {
  const { t, i18n } = useTranslation();
  const { rideId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ride, setRide] = useState<RideRow | null>(null);
  const [creator, setCreator] = useState<Profile | null>(null);
  const [riders, setRiders] = useState<Profile[]>([]);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState<null | { type: "user" | "ride"; id: string; label: string }>(null);
  const [club, setClub] = useState<ClubInfo | null>(null);
  const [clubLogoUrl, setClubLogoUrl] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [port, setPort] = useState<PortInfo | null>(null);
  const [portCompleted, setPortCompleted] = useState(false);

  const reportReasonKeys = ["inappropriate", "dangerous", "spam", "false", "other"] as const;

  const submitReport = async () => {
    if (!user) { navigate({ to: "/auth" }); return; }
    if (!reportOpen || !reportReason) { toast.error(t("ride.reportSelectReason")); return; }
    setReportSending(true);
    const { error } = await supabase.from("reports" as any).insert({
      reporter_id: user.id,
      target_type: reportOpen.type,
      target_id: reportOpen.id,
      reason: reportReason,
      details: reportDetails || null,
    });
    setReportSending(false);
    if (error) { toast.error(error.message); return; }
    toast.success(t("ride.reportThanks"));
    setReportOpen(null);
    setReportReason("");
    setReportDetails("");
  };

  const load = async () => {
    const { data: r } = await supabase.from("rides").select("*").eq("id", rideId).single();
    if (!r) return;
    setRide(r as RideRow);
    const { data: c } = await supabase.from("profiles").select("id, nickname, avatar_url").eq("id", r.creator_id).single();
    setCreator(c as Profile);
    const { data: parts } = await supabase.from("ride_participants").select("user_id").eq("ride_id", rideId);
    const ids = (parts || []).map((p: any) => p.user_id);
    if (ids.length) {
      const { data: ps } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids);
      setRiders((ps as Profile[]) || []);
    } else setRiders([]);
  };

  useEffect(() => { load(); }, [rideId]);

  useEffect(() => {
    const ch = supabase
      .channel(`ride-${rideId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ride_participants", filter: `ride_id=eq.${rideId}` },
        () => load()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [rideId]);

  useEffect(() => {
    if (!ride) return;
    fetch(`https://wttr.in/${ride.start_lat},${ride.start_lng}?format=j1`)
      .then((r) => r.json())
      .then((d) => {
        const cur = d.current_condition?.[0];
        if (cur) setWeather({
          temp: `${cur.temp_C}°C`,
          wind: `${cur.windspeedKmph} km/h`,
          rain: `${cur.chanceofrain ?? "0"}%`,
        });
      })
      .catch(() => {});
  }, [ride?.id]);

  useEffect(() => {
    const clubId = (ride as any)?.club_id as string | null | undefined;
    if (!clubId) { setClub(null); setClubLogoUrl(null); return; }
    (async () => {
      const { data } = await supabase
        .from("clubs" as any)
        .select("id, name, logo_url, is_verified")
        .eq("id", clubId)
        .single();
      const c = data as ClubInfo | null;
      if (!c || !c.is_verified) { setClub(null); setClubLogoUrl(null); return; }
      setClub(c);
      if (c.logo_url) {
        const { data: signed } = await supabase.storage.from("club-logos").createSignedUrl(c.logo_url, 3600);
        setClubLogoUrl(signed?.signedUrl ?? null);
      } else {
        setClubLogoUrl(null);
      }
    })();
  }, [ride?.club_id]);

  useEffect(() => {
    const portId = ride?.port_id;
    if (!portId) { setPort(null); setPortCompleted(false); return; }
    (async () => {
      const { data: p } = await supabase
        .from("ports")
        .select("id,name,slope_side,length_km,elevation_gain_m,avg_gradient,category,surface,altitude_m")
        .eq("id", portId)
        .single();
      setPort(p as PortInfo | null);
      if (user) {
        const { data: pc } = await supabase
          .from("port_completions")
          .select("id")
          .eq("port_id", portId)
          .eq("user_id", user.id)
          .eq("status", "approved")
          .single();
        setPortCompleted(!!pc);
      } else {
        setPortCompleted(false);
      }
    })();
  }, [ride?.port_id, user?.id]);

  if (!ride) {
    return (
      <div className="min-h-screen bg-background p-5 space-y-4">
        <div className="h-10 w-40 animate-pulse rounded-xl bg-surface-elevated" />
        <div className="h-8 w-24 animate-pulse rounded-full bg-surface-elevated" />
        <div className="h-12 w-2/3 animate-pulse rounded-xl bg-surface-elevated" />
        <div className="h-44 w-full animate-pulse rounded-2xl bg-surface-elevated" />
        <div className="grid grid-cols-3 gap-2">
          <div className="h-16 animate-pulse rounded-2xl bg-surface-elevated" />
          <div className="h-16 animate-pulse rounded-2xl bg-surface-elevated" />
          <div className="h-16 animate-pulse rounded-2xl bg-surface-elevated" />
        </div>
      </div>
    );
  }

  const joined = !!user && riders.some((r) => r.id === user.id);
  const isCreator = user?.id === ride.creator_id;

  const join = async () => {
    if (!user) return navigate({ to: "/auth" });
    setBusy(true);
    // optimistic
    const me: Profile = { id: user.id, nickname: "…", avatar_url: null };
    setRiders((rs) => [...rs, me]);
    const { error } = await supabase.from("ride_participants").insert({ ride_id: ride.id, user_id: user.id });
    if (error) {
      setRiders((rs) => rs.filter((r) => r.id !== user.id));
      toast.error(error.message);
    } else {
      toast.success(t("ride.joinedToast"));
      load();
    }
    setBusy(false);
  };
  const leave = async () => {
    if (!user) return;
    setBusy(true);
    setRiders((rs) => rs.filter((r) => r.id !== user.id));
    const { error } = await supabase.from("ride_participants").delete().eq("ride_id", ride.id).eq("user_id", user.id);
    if (error) toast.error(error.message); else { toast.success(t("ride.leftToast")); load(); }
    setBusy(false);
  };
  const cancel = () => setCancelOpen(true);

  const confirmCancel = async () => {
    if (!ride) return;
    setCancelBusy(true);
    const { error } = await supabase
      .from("rides")
      .update({ status: "cancelled", cancelled_reason: cancelReason.trim() || null })
      .eq("id", ride.id);
    setCancelBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setCancelOpen(false);
    setCancelReason("");
    toast.success(t("ride.cancelled"));
    navigate({ to: "/map" });
  };

  const mLabel = MODALITIES.find((m) => m.value === ride.modality)?.label;
  const mColor = ride.modality === "road" ? "#F5C518" : ride.modality === "gravel" ? "#e07b3a" : ride.modality === "mtb" ? "#4caf7d" : "#3b82f6";
  const localeMap: Record<string, string> = { es: "es-ES", ca: "ca-ES", en: "en-US" };
  const dateLocale = localeMap[i18n.language] ?? "es-ES";

  return (
    <div className="flex h-[100dvh] flex-col bg-background fade-in">
      <header className="glass-panel flex shrink-0 items-center gap-3 px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <Link to="/map" className="rounded-full bg-surface p-2"><ArrowLeft className="h-5 w-5" /></Link>
        <h1 className="font-display text-xl">{t("ride.title")}</h1>
      </header>

      <div className="flex-1 overflow-y-auto space-y-5 px-5 py-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider"
              style={{ background: `color-mix(in oklab, ${mColor} 22%, transparent)`, color: mColor }}
            >{mLabel}</span>
            <span className="rounded-full bg-surface-elevated px-2.5 py-1 text-xs font-medium">{paceLabel(ride.pace)}</span>
          </div>
          <div className="mt-3 font-display text-3xl">
            {relativeMinutes(ride.start_time)}
          </div>
          <div className="text-sm text-muted-foreground">
            {new Date(ride.start_time).toLocaleString(dateLocale, { weekday: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" })}
          </div>
          {creator && (
            <div className="mt-3 flex items-center gap-2">
              <Link
                to="/profile/$userId" params={{ userId: creator.id }}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-elevated text-xs font-semibold">
                  {creator.nickname[0]?.toUpperCase()}
                </span>
                {t("ride.createdBy")} <span className="font-medium text-foreground">@{creator.nickname}</span>
              </Link>
              {user && user.id !== creator.id && (
                <button
                  onClick={() => setReportOpen({ type: "user", id: creator.id, label: `@${creator.nickname}` })}
                  className="ml-auto rounded-full bg-surface p-1.5 text-muted-foreground hover:text-destructive"
                  aria-label={t("ride.reportUser")}
                >
                  <Flag className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-border" style={{ height: 180 }}>
          <RideMap
            rides={[{ id: ride.id, modality: ride.modality, lat: ride.start_lat, lng: ride.start_lng }]}
            center={[ride.start_lat, ride.start_lng]}
          />
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat icon={<MapPin className="h-4 w-4" />} label={t("ride.departureShort")} value={ride.start_address?.split(",")[0] ?? "—"} />
          <Stat icon={<Clock className="h-4 w-4" />} label={t("ride.distance")} value={ride.estimated_km ? `${ride.estimated_km} km` : "—"} />
          <Stat icon={<Clock className="h-4 w-4" />} label={t("ride.duration")} value={ride.estimated_duration_min ? `${ride.estimated_duration_min}'` : "—"} />
        </div>

        {port && (
          <Link
            to="/puerto/$id"
            params={{ id: port.id }}
            className="group relative block rounded-2xl border border-border bg-surface p-4 transition-colors hover:bg-surface-elevated"
          >
            {portCompleted && (
              <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                {t("ride.portCard.completed")}
              </span>
            )}
            <div className="mb-1 flex items-center gap-2">
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold"
                style={{ background: categoryColor(port.category), color: "#0f0f0f" }}
              >
                {port.category || "—"}
              </span>
              <div className="font-display text-base font-semibold">{port.name}</div>
            </div>
            <div className="text-sm text-muted-foreground">
              {port.slope_side}
              {port.slope_side && (port.length_km || port.elevation_gain_m || port.avg_gradient || port.surface) && " · "}
              {port.length_km !== null && `${Number(port.length_km).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`}
              {port.length_km !== null && port.elevation_gain_m !== null && " · "}
              {port.elevation_gain_m !== null && `${Number(port.elevation_gain_m).toLocaleString(i18n.language)} m D+`}
              {port.elevation_gain_m !== null && port.avg_gradient !== null && " · "}
              {port.avg_gradient !== null && `${Number(port.avg_gradient).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
              {port.avg_gradient !== null && port.surface && " · "}
              {port.surface && (port.surface.charAt(0).toUpperCase() + port.surface.slice(1))}
            </div>
            {port.altitude_m !== null && (
              <div className="mt-1 text-xs text-muted-foreground">
                {t("map.altitude")}: {Number(port.altitude_m).toLocaleString(i18n.language)} m
              </div>
            )}
          </Link>
        )}

        {ride.description && (
          <div className="rounded-2xl border border-border bg-surface p-4 text-sm leading-relaxed">
            {ride.description}
          </div>
        )}

        {weather && (
          <div className="rounded-2xl border border-border bg-surface p-4">
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">{t("ride.weather")}</div>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-2"><Thermometer className="h-4 w-4 text-primary" />{weather.temp}</span>
              <span className="flex items-center gap-2"><Wind className="h-4 w-4 text-primary" />{weather.wind}</span>
              <span className="flex items-center gap-2"><Droplets className="h-4 w-4 text-primary" />{weather.rain}</span>
            </div>
          </div>
        )}

        {club && (
          <div className="rounded-2xl border border-border bg-surface p-4">
            <div className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">Organizada por</div>
            <div className="flex items-center gap-3">
              {clubLogoUrl ? (
                <img src={clubLogoUrl} alt={club.name} className="h-12 w-12 rounded-full object-cover border border-border" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-sm font-bold">
                  {club.name[0]?.toUpperCase()}
                </div>
              )}
              <div className="font-medium">{club.name}</div>
            </div>
          </div>
        )}



        <div className="rounded-2xl border border-border bg-surface p-4">
          <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> {t("ride.riders")} ({riders.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {riders.length === 0 && <span className="text-sm text-muted-foreground">{t("ride.beFirst")}</span>}
            {riders.map((r) => (
              <Link
                key={r.id}
                to="/profile/$userId" params={{ userId: r.id }}
                className="flex items-center gap-2 rounded-full bg-surface-elevated px-3 py-1.5 hover:bg-surface transition-colors"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                  {r.nickname[0]?.toUpperCase()}
                </span>
                <span className="text-xs">@{r.nickname}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-border bg-background/90 backdrop-blur-md p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <p className="mb-2 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{t("ride.disclaimer")}</span>
        </p>
        {isCreator ? (
          <div className="space-y-2">
            <div className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary py-4 font-bold text-primary">
              <Settings2 className="h-5 w-5" /> {t("ride.yourRide")}
            </div>
            {(ride as any).status === "active" && new Date(ride.start_time).getTime() > Date.now() + 30 * 60 * 1000 && (
              <button
                onClick={() => setEditOpen(true)}
                className="press-scale block w-full rounded-xl bg-surface-elevated py-3 text-center text-sm font-semibold"
              >
                {t("ride.editRide")}
              </button>
            )}
            <button onClick={cancel} className="press-scale block w-full rounded-xl border border-destructive/30 py-3 text-center text-sm font-semibold text-destructive">
              {t("ride.cancelRide")}
            </button>
          </div>
        ) : joined ? (
          <div className="space-y-2">
            <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary/15 py-4 font-bold text-primary">
              <Check className="h-5 w-5" strokeWidth={3} /> {t("ride.joined")}
            </div>
            <button onClick={leave} disabled={busy} className="press-scale block w-full py-1 text-center text-xs font-medium text-white/60">
              {t("ride.cancel")}
            </button>
          </div>
        ) : (
          <button onClick={join} disabled={busy} className="press-scale flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-4 font-bold text-primary-foreground disabled:opacity-50">
            {busy ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : <><UserPlus className="h-5 w-5" /> {t("ride.joinAction")}</>}
          </button>
        )}
        {!isCreator && (
          <button
            onClick={() => setReportOpen({ type: "ride", id: ride.id, label: t("ride.reportRide") })}
            className="mt-2 flex w-full items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground hover:text-destructive"
          >
            <Flag className="h-3 w-3" /> {t("ride.reportRide")}
          </button>
        )}
      </div>

      <Sheet open={!!reportOpen} onOpenChange={(o) => !o && setReportOpen(null)}>
        <SheetContent side="bottom" className="bg-[#1a1a1a] border-t border-[#2a2a2a] text-white">
          <SheetHeader>
            <SheetTitle className="text-white">{t("ride.report")} {reportOpen?.label}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              {reportReasonKeys.map((key) => {
                const label = t(`ride.reportReasons.${key}` as any);
                return (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="report-reason"
                      value={key}
                      checked={reportReason === key}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="accent-[#F5C518]"
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t("ride.reportDetailsPlaceholder")}
              className="w-full resize-none rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] p-3 text-sm text-white outline-none focus:border-[#F5C518]"
            />
            <button
              onClick={submitReport}
              disabled={reportSending || !reportReason}
              className="w-full rounded-xl bg-[#F5C518] py-3 font-bold text-black disabled:opacity-50"
            >
              {reportSending ? t("ride.reportSending") : t("ride.reportSend")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={cancelOpen} onOpenChange={(o) => { if (!o) { setCancelOpen(false); setCancelReason(""); } }}>
        <SheetContent side="bottom" className="bg-[#1a1a1a] border-t border-[#2a2a2a] text-white">
          <SheetHeader>
            <SheetTitle className="text-white">{t("ride.cancelConfirm")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t("ride.cancelReasonPlaceholder")}
              className="w-full resize-none rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] p-3 text-sm text-white outline-none focus:border-[#F5C518]"
            />
            <button
              onClick={confirmCancel}
              disabled={cancelBusy}
              className="flex w-full items-center justify-center rounded-xl bg-destructive py-3 font-bold text-white disabled:opacity-50"
            >
              {cancelBusy ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : t("ride.cancelYes")}
            </button>
            <button
              onClick={() => { setCancelOpen(false); setCancelReason(""); }}
              disabled={cancelBusy}
              className="w-full rounded-xl border border-[#2a2a2a] py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t("ride.cancel")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {isCreator && ride && (
        <EditRideSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          rideId={ride.id}
          initial={{
            start_time: ride.start_time,
            pace: ride.pace as any,
            estimated_km: ride.estimated_km,
            estimated_duration_min: ride.estimated_duration_min,
            description: ride.description,
          }}
        />
      )}
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-3">
      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 truncate font-display text-base">{value}</div>
    </div>
  );
}
